import { CreateChatFormData } from '@choco-admin/types';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { Input } from '../../../../components/FormComponents';
import { FORM_LABELS, FORM_PLACEHOLDERS } from '../../constants/chatOptions';

interface ChatFormInputFieldProps {
  name: keyof CreateChatFormData;
  label?: string;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel' | 'password' | 'number' | 'url';
  isRequired?: boolean;
  customerNumberMandatory?: boolean | null;
  chatNameRef?: React.MutableRefObject<HTMLInputElement | null>;
  /** Additional CSS classes */
  className?: string;
}

export const ChatFormInputField: React.FC<ChatFormInputFieldProps> = ({
  name,
  label,
  placeholder,
  type = 'text',
  isRequired = false,
  customerNumberMandatory,
  chatNameRef,
  className = '',
}) => {
  const {
    register,
    formState: { errors },
    watch,
  } = useFormContext<CreateChatFormData>();

  const fieldLabel =
    label || FORM_LABELS[name as keyof typeof FORM_LABELS] || name;
  const fieldPlaceholder =
    placeholder ||
    FORM_PLACEHOLDERS[name as keyof typeof FORM_PLACEHOLDERS] ||
    '';

  // Special handling for customer number required state
  const isFieldRequired =
    name === 'customerNumber'
      ? customerNumberMandatory || isRequired
      : isRequired;

  const errorMessage = errors[name]?.message as string | undefined;
  const fieldValue = watch(name) as string;

  // Register the field with react-hook-form
  const { ref, onChange, ...registerProps } = register(name, {
    required: isFieldRequired ? `${fieldLabel} is required` : false,
  });

  return (
    <div className={`space-y-2 ${className}`}>
      <Input
        {...registerProps}
        id={name}
        name={name}
        label={fieldLabel}
        placeholder={fieldPlaceholder}
        type={type}
        value={fieldValue || ''}
        required={isFieldRequired}
        isInvalid={!!errorMessage}
        errorMessage={errorMessage}
        onChange={(value) => onChange({ target: { name, value } })}
      />

      {name === 'additionalEmails' && (
        <p className="text-sm text-foreground-500">
          Separate multiple email addresses with commas
        </p>
      )}

      {name === 'customerNumber' && customerNumberMandatory && (
        <p className="text-sm text-warning-600">
          This distributor requires a customer number
        </p>
      )}
    </div>
  );
};

ChatFormInputField.displayName = 'ChatFormInputField';
